import React, { useContext, useEffect, useRef } from 'react';
import { useLocation, useParams } from '@thd-olt-component-react/router';

import { StoreFinderRedirector } from '@thd-nucleus/app-render';
import { QueryContext, extend } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { EmtLinks } from '@thd-olt-component-react/emt-links';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { declareContexts, Hydrator } from '@thd-olt-component-react/hydrator';
import { StoreReviewsMetadata } from '@thd-olt-component-react/metadata';
/*
  These imports might change once all the experience are migrated to vite.
  Currently they have webpack and vite components co-existing.
*/
import {
  StoreRatingsAndReviewsDesktopImporterVite as StoreRatingsAndReviewsDesktopImporter,
  StoreRatingsAndReviewsMobileImporterVite as StoreRatingsAndReviewsMobileImporter
} from '@thd-olt-component-react/ratings-and-reviews';
import { PopularCategories } from '@thd-olt-component-react/related-search';
import {
  StoreHero,
  StoreHeroCurbside,
  StoreHeroDetails,
  StoreHeroFeatures,
  StoreHeroHeader,
  StoreHeroWorkshop
} from '@thd-olt-component-react/store-hero';
import { GrandOpeningContainer } from '@thd-olt-component-react/emt-generated-content';
import { NearbyStores } from '../../shared-components/NearbyStores';
import { StoreFinderBreadcrumbs } from '../../shared-components/StoreFinderBreadcrumbs';

import { useMobileBreakpoint } from '../../utils/stylesUtils';

export const Reviews = () => {
  declareContexts([QueryContext, ExperienceContext], { freeze: true });

  const { channel } = useContext(ExperienceContext);
  const path = useLocation();
  const ratingsReviewsRef = useRef();
  const {
    storeNumber, storeZip, name, state, city, reviewPage
  } = useParams();
  const isMobileBreakpoint = useMobileBreakpoint();

  const seoPageNumber = reviewPage ? parseInt(reviewPage, 10) : 1;
  const isMobileChannel = channel === 'mobile';

  const onRatingClick = () => {
    ratingsReviewsRef.current.scrollIntoView();
  };

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('store-finder.reviews-page-load', {
      storeNumber,
      storeZip,
      state,
      name,
      city
    });
  }, [storeNumber]);

  const headerFont = {
    size: isMobileChannel ? 'medium' : 'xlarge'
  };

  return (
    <>
      <ErrorBoundary name="sf-reviews-redirector">
        <StoreFinderRedirector />
      </ErrorBoundary>

      <ErrorBoundary name="sf-reviews-metadata">
        <StoreReviewsMetadata
          storeId={storeNumber}
          seoPageNumber={seoPageNumber}
        />
      </ErrorBoundary>

      <div className="sui-max-w-screen-2xl sui-m-auto">
        <div className="sui-flex sui-flex-col sui-px-2 2xl:sui-px-0">
          <ErrorBoundary name="reviews-breadcrumbs">
            <StoreFinderBreadcrumbs
              includeDirectory
              pageName="Reviews"
            />
          </ErrorBoundary>
          <ErrorBoundary name="store-reviews-grand-opening-pod">
            <GrandOpeningContainer
              contentId={storeNumber}
              pageName="storeReviews"
            />
          </ErrorBoundary>
          <ErrorBoundary name="reviews-store-hero">
            <StoreHero storeId={storeNumber}>
              <StoreHeroHeader
                label="Reviews for "
              />

              <section className="sui-flex sui-flex-col lg:sui-grid lg:sui-grid-cols-3 sui-gap-3 sui-pb-2">
                <StoreHeroDetails contentId={storeNumber} pageName="storeReviews" />
                <StoreHeroCurbside />
                {isMobileChannel && <StoreHeroWorkshop />}
                <StoreHeroFeatures showServices={isMobileBreakpoint} />
              </section>
            </StoreHero>
          </ErrorBoundary>

          <ErrorBoundary name="reviews-ratings-and-reviews">
            <div ref={ratingsReviewsRef} className="sui-flex sui-my-4">
              {!isMobileChannel && (
                <StoreRatingsAndReviewsDesktopImporter
                  storeId={storeNumber}
                  category="store"
                  seoPageNumber={seoPageNumber}
                  dynamic={{
                    pageType: 'store-reviews'
                  }}
                  hydrator={{
                    className: 'sui-bg-primary'
                  }}
                />
              )}
              {isMobileChannel && (
                <StoreRatingsAndReviewsMobileImporter
                  storeId={storeNumber}
                  category="store"
                  seoPageNumber={seoPageNumber}
                  seoStoreLink={path.pathname}
                  dynamic={{
                    pageType: 'store-reviews'
                  }}
                  hydrator={{
                    className: 'sui-bg-primary',
                  }}
                />
              )}
            </div>
          </ErrorBoundary>

          <ErrorBoundary name="reviews-nearby-stores">
            <div className="sui-my-4">
              <Hydrator
                id="reviews-nearby-stores-map"
                scrollBuffer={800}
              >
                <NearbyStores
                  storeNumber={storeNumber}
                  omitStoreNumber={storeNumber}
                />
              </Hydrator>
            </div>
          </ErrorBoundary>

          <ErrorBoundary name="reviews-popular-categories">
            <PopularCategories
              contentId={storeNumber}
              pageType="storeReviews"
            />
          </ErrorBoundary>

          <ErrorBoundary name="reviews-emt-links">
            <EmtLinks path={path.pathname} />
          </ErrorBoundary>
        </div>
      </div>
    </>
  );
};

Reviews.displayName = 'Reviews';
Reviews.propTypes = {};

Reviews.dataModel = extend(
  EmtLinks,
  GrandOpeningContainer,
  NearbyStores,
  PopularCategories,
  StoreFinderBreadcrumbs,
  StoreFinderRedirector,
  StoreHero,
  StoreRatingsAndReviewsDesktopImporter,
  StoreRatingsAndReviewsMobileImporter,
  StoreReviewsMetadata,
);
